import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLandingPageVariant } from '../../../contexts/LandingPageVariantContext';
import { DEFAULT_PLACEHOLDER } from '../constants';

/**
 * Manages input-area UI state: textarea auto-resize, dynamic placeholder text,
 * pill bounds measurement, and derived `hasInput` / `isLoading` flags.
 *
 * @param {Object} params
 * @param {string} params.query - Current query text.
 * @param {Object} params.attachments - Attachments hook return value.
 * @param {Object|null} params.selectedBook - Currently selected book.
 * @param {Object|null} params.selectedTemplate - Currently selected template.
 * @param {boolean} params.hasSelectedStandards - Whether any standards are selected.
 * @param {Object[]} params.userPills - Array of helper pill objects.
 * @param {Object} [params.inputRef] - Optional shared React ref for the input element.
 * @returns {{ inputRef, adjustTextareaHeight, inputPlaceholder, isDefaultPlaceholder, hasInput,
 *   pillsRef, pillBounds }} `isDefaultPlaceholder` is true only when no contextual
 *   placeholder applies — i.e. the plain "what do you need" copy is showing.
 */
export const useInputUI = ({
  query,
  attachments,
  selectedBook,
  selectedTemplate,
  hasSelectedStandards,
  userPills,
  inputRef: externalInputRef,
}) => {
  const { unlimitedInputCopyVariations } = useFlags();
  const landingPageVariant = useLandingPageVariant();
  const internalRef = useRef(null);
  const inputRef = externalInputRef || internalRef;
  const pillsRef = useRef(null);
  const [pillBounds, setPillBounds] = useState(null);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 150;
    let scrollHeight = textarea.scrollHeight;
    if (!textarea.value && textarea.placeholder) {
      textarea.value = textarea.placeholder;
      scrollHeight = Math.max(scrollHeight, textarea.scrollHeight);
      textarea.value = '';
    }
    textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  // Dynamic placeholder. `isDefaultPlaceholder` is derived here rather than
  // recomputed by callers so the two can't drift: the contextual branches key on
  // signals hasInput doesn't track (attachedLinks at all, and pdfFile/imageFiles
  // before their S3 upload resolves), so anything gating on "is the plain
  // placeholder showing" has to come from this same decision.
  const { inputPlaceholder, isDefaultPlaceholder } = useMemo(() => {
    const contextual = (text) => ({ inputPlaceholder: text, isDefaultPlaceholder: false });
    if (selectedTemplate) return contextual('What do you want to create with this template?');
    if (attachments.pdfFile || selectedBook || attachments.attachedLinks.length > 0)
      return contextual('How would you like to adapt or extend this content?');
    if (attachments.imageFiles.length > 0)
      return contextual(
        attachments.imageFiles.length === 1
          ? 'What would you like to do with this image?'
          : `What would you like to do with these ${attachments.imageFiles.length} images?`
      );
    return {
      // Variant copy (campaign landing pages) outranks the LD flag, which
      // outranks the default — same precedence chain as the headline.
      inputPlaceholder:
        landingPageVariant?.placeholder ||
        unlimitedInputCopyVariations?.placeholder ||
        DEFAULT_PLACEHOLDER,
      isDefaultPlaceholder: true,
    };
  }, [
    selectedTemplate,
    attachments.pdfFile,
    selectedBook,
    attachments.attachedLinks.length,
    attachments.imageFiles.length,
    unlimitedInputCopyVariations?.placeholder,
    landingPageVariant?.placeholder,
  ]);

  // Re-measure textarea when query or placeholder changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [query, inputPlaceholder, adjustTextareaHeight]);

  // The height is derived from how many lines the copy wraps to, so it goes
  // stale whenever the box gets narrower or the glyphs change size — neither of
  // which touches `query` or `inputPlaceholder`. `document.fonts.ready` covers
  // a web font landing after the first measurement ran against the fallback
  // face.
  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready
      ?.then(() => {
        if (!cancelled) adjustTextareaHeight();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [adjustTextareaHeight]);

  // The textarea is reached through a ref object (consumers pass their own in
  // and attach it themselves), so nothing re-renders this hook when the element
  // mounts, unmounts with its view, or comes back as a new node. Mirror
  // `inputRef.current` into state after every render — identical values bail
  // out before re-rendering — so the observer below can key on the element
  // actually mounted instead of whatever was there at first run.
  const [textareaNode, setTextareaNode] = useState(null);
  useEffect(() => {
    setTextareaNode((node) => (node === inputRef.current ? node : inputRef.current));
  });

  // A ResizeObserver on the textarea catches container-driven width changes as
  // well as viewport resizes.
  useEffect(() => {
    if (!textareaNode || typeof ResizeObserver === 'undefined') return;

    // adjustTextareaHeight writes `height`, which re-triggers the observer, so
    // only a width change is treated as a reason to measure again.
    let lastWidth = textareaNode.clientWidth;
    const observer = new ResizeObserver(() => {
      const width = textareaNode.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      adjustTextareaHeight();
    });
    observer.observe(textareaNode);
    return () => observer.disconnect();
  }, [textareaNode, adjustTextareaHeight]);

  // Focus input when template is selected
  useEffect(() => {
    if (selectedTemplate) inputRef.current?.focus();
  }, [selectedTemplate]);

  // Measure pill bounds for aligning suggestions
  useEffect(() => {
    if (!pillsRef.current) {
      setPillBounds(null);
      return;
    }
    const measure = () => {
      const container = pillsRef.current;
      if (!container) return;
      if (window.innerWidth < 848) {
        setPillBounds({ left: 9, right: 0 });
        return;
      }
      const buttons = container.querySelectorAll('button');
      if (buttons.length === 0) return;
      const parentRect = container.closest('.max-w-3xl')?.getBoundingClientRect();
      if (!parentRect) return;
      const firstBtn = buttons[0];
      const firstTop = firstBtn.offsetTop;
      let lastBtn = firstBtn;
      for (const btn of buttons) {
        if (btn.offsetTop === firstTop) lastBtn = btn;
        else break;
      }
      const firstRect = firstBtn.getBoundingClientRect();
      const lastRect = lastBtn.getBoundingClientRect();
      const pillPadL = parseFloat(getComputedStyle(firstBtn).paddingLeft);
      const pillPadR = parseFloat(getComputedStyle(lastBtn).paddingRight);
      const suggPad = 16;
      setPillBounds({
        left: firstRect.left - parentRect.left + pillPadL - suggPad,
        right: parentRect.right - lastRect.right + pillPadR - suggPad,
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [userPills]);

  // Derived flags
  const hasInput =
    query.trim().length > 0 ||
    attachments.uploadedPdfURL ||
    attachments.uploadedImageURLs.length > 0 ||
    selectedBook ||
    selectedTemplate ||
    hasSelectedStandards;

  return useMemo(
    () => ({
      inputRef,
      adjustTextareaHeight,
      inputPlaceholder,
      isDefaultPlaceholder,
      hasInput,
      pillsRef,
      pillBounds,
    }),
    [adjustTextareaHeight, inputPlaceholder, isDefaultPlaceholder, hasInput, pillBounds]
  );
};
